import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Crown, Copy, Check, ScanFace, Zap, Infinity, Calendar, Shield, Sparkles, Loader2, ArrowUpRight, Gift, User as UserIcon, CreditCard, Coins } from "lucide-react";

const PERKS = [
  { icon: Crown, title: "ASCENDED Badge", desc: "Visible on your profile, on the leaderboard, and every mog battle." },
  { icon: ScanFace, title: "ELITE Scan Mode", desc: "24 sub-metrics, celebrity mog-match, canthal-tilt in degrees, ideal BF% & weight targets, 10 ranked actions." },
  { icon: Zap, title: "Unlimited & Faster Re-scans", desc: "Priority queue. Scan as often as you want. Track your ascension frame by frame." },
  { icon: Calendar, title: "Events Access (2027+)", desc: "Front-row access to Ascend meetups, workshops, and PSL-tier retreats when they launch." },
  { icon: Shield, title: "Priority Support", desc: "Direct-line to the team. Bug reports and feature requests jump the queue." },
  { icon: Sparkles, title: "Early Access", desc: "New features hit VIPs first — before they roll out to the public." },
];

export default function VIP() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [cardPlans, setCardPlans] = useState([]);
  const [wallet, setWallet] = useState("");
  const [order, setOrder] = useState(null);
  const [signature, setSignature] = useState("");
  const [creating, setCreating] = useState(false);
  const [cardBusy, setCardBusy] = useState(null); // plan_id being processed
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(null);
  const [status, setStatus] = useState(null);
  const [giftMode, setGiftMode] = useState(false);
  const [giftUsername, setGiftUsername] = useState("");

  useEffect(() => {
    api.get("/vip/plans").then((r) => {
      setPlans(r.data.plans);
      setWallet(r.data.wallet_address);
    });
    api.get("/vip/status").then((r) => setStatus(r.data));
    api.get("/payments/vip/plans").then((r) => setCardPlans(r.data)).catch(() => {});
  }, []);

  const gbpFor = (planId) => cardPlans.find((p) => p.id === planId)?.gbp;

  const payWithCard = async (planId) => {
    setCardBusy(planId);
    try {
      const { data } = await api.post("/payments/vip/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start card checkout");
      setCardBusy(null);
    }
  };

  const startOrder = async (planId) => {
    setCreating(true);
    try {
      const body = { plan_id: planId };
      if (giftMode) {
        const clean = giftUsername.trim().replace(/^@/, "");
        if (!clean) {
          toast.error("Enter a username to gift to");
          setCreating(false);
          return;
        }
        body.recipient_username = clean;
      }
      const { data } = await api.post("/vip/quote", body);
      setOrder(data);
      setSignature("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create order");
    } finally {
      setCreating(false);
    }
  };

  const copy = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    toast.success("Copied.");
    setTimeout(() => setCopied(null), 1600);
  };

  const verify = async () => {
    const sig = signature.trim();
    if (!sig) {
      toast.error("Paste your Solana transaction signature");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.post("/vip/verify", { order_id: order.order_id, signature: sig });
      if (data.gifted_to) {
        toast.success(`Gift delivered to @${data.gifted_to}. Halo secured.`);
      } else {
        toast.success("Welcome, ASCENDED.");
      }
      await refresh?.();
      setStatus({
        is_vip: !data.gifted_to ? true : status?.is_vip,
        vip_tier: !data.gifted_to ? data.vip_tier : status?.vip_tier,
        vip_expires_at: !data.gifted_to ? data.vip_expires_at : status?.vip_expires_at,
      });
      setOrder(null);
      setSignature("");
      setGiftMode(false);
      setGiftUsername("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const isVip = status?.is_vip;

  return (
    <div className="max-w-[1300px] mx-auto px-6 py-10 relative z-10">
      <div className="mb-10">
        <span className="tape-label">MEMBERSHIP</span>
        <h1 className="font-display text-5xl md:text-8xl mt-3 leading-none">
          BECOME<br />
          <span className="text-[var(--primary)] glow">ASCENDED.</span>
        </h1>
        <p className="text-[var(--text-muted)] mt-4 max-w-xl">
          Elite scanner. Unlimited re-scans. The badge. Event access. Pay with card or SOL — your call.
        </p>
      </div>

      {isVip && !order && (
        <div className="panel-bright p-6 mb-8 flex items-center justify-between border-l-4 border-[var(--primary)]" data-testid="vip-active-banner">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-[var(--primary)] glow" />
              <span className="tape-label">ACTIVE</span>
            </div>
            <div className="font-display text-3xl">YOU ARE ASCENDED.</div>
            <div className="text-xs font-mono text-[var(--text-muted)] mt-1">
              Tier: <span className="text-[var(--primary)]">{status.vip_tier?.toUpperCase()}</span>
              {status.vip_expires_at
                ? <> · Expires {new Date(status.vip_expires_at).toLocaleDateString()}</>
                : <> · Lifetime <Infinity className="inline w-3 h-3 ml-1" /></>}
            </div>
          </div>
          <Link to="/scan" className="btn-brutal" data-testid="vip-elite-scan-cta">
            Run Elite Scan <ArrowUpRight className="inline w-4 h-4 ml-1" />
          </Link>
        </div>
      )}

      {/* Perks */}
      <div className="grid grid-cols-12 gap-px bg-[var(--border)] mb-10">
        {PERKS.map(({ icon: Icon, title, desc }, i) => (
          <div key={title} className="col-span-12 md:col-span-6 lg:col-span-4 bg-[var(--bg)] p-6" data-testid={`vip-perk-${i}`}>
            <Icon className="w-6 h-6 text-[var(--primary)] mb-3 glow" />
            <div className="font-display text-2xl tracking-wide mb-2">{title}</div>
            <p className="text-sm text-[var(--text-muted)]">{desc}</p>
          </div>
        ))}
      </div>

      {/* Plans */}
      {!order && (
        <>
          {/* Gift mode toggle */}
          <div className="panel-bright p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Gift className="w-5 h-5 text-[var(--primary)]" />
              <div>
                <div className="font-display text-xl">
                  {giftMode ? "GIFTING VIP" : "GIFT VIP TO ANOTHER USER"}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                  {giftMode ? "The badge and perks will land on their account." : "Buy for yourself OR gift it to another ascender."}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {giftMode && (
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
                  <input
                    value={giftUsername}
                    onChange={(e) => setGiftUsername(e.target.value)}
                    placeholder="@username"
                    className="brutal pl-9 max-w-xs"
                    data-testid="gift-username-input"
                  />
                </div>
              )}
              <button
                type="button"
                onClick={() => setGiftMode(!giftMode)}
                data-testid="gift-toggle"
                aria-pressed={giftMode}
                className={`relative w-12 h-6 border transition-colors shrink-0 ${
                  giftMode ? "bg-[var(--primary)] border-[var(--primary)]" : "bg-[var(--surface)] border-[var(--border-bright)]"
                }`}
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-black transition-all ${giftMode ? "left-[1.625rem]" : "left-0.5"}`} />
              </button>
            </div>
          </div>

          {!isVip && (
            <div className="grid grid-cols-12 gap-6 mb-10">
              {plans.map((p) => {
                const lifetime = p.duration_days === null;
                return (
                  <div
                    key={p.id}
                    className={`col-span-12 md:col-span-6 panel-bright p-8 relative ${lifetime ? "border-[var(--primary)]" : ""}`}
                    data-testid={`vip-plan-${p.id}`}
                  >
                    {lifetime && (
                      <span className="absolute -top-3 left-6 tape-label" style={{ background: "var(--primary)" }}>
                        Best value
                      </span>
                    )}
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                      {giftMode ? `GIFT ${p.label.toUpperCase()}` : p.label}
                    </div>
                    <div className="flex items-baseline gap-3 mt-3">
                      <div className="font-display text-7xl text-[var(--primary)] glow">{p.sol}</div>
                      <div className="font-mono text-xl text-white">SOL</div>
                    </div>
                    <div className="text-xs font-mono text-[var(--text-muted)] mt-2 mb-6">
                      {lifetime ? (
                        <span className="flex items-center gap-1"><Infinity className="w-3 h-3" /> Forever + all future events</span>
                      ) : (
                        <>{p.duration_days} days of VIP</>
                      )}
                    </div>
                    <p className="text-sm text-[var(--text-muted)] mb-6">{p.description}</p>
                    <button
                      onClick={() => startOrder(p.id)}
                      disabled={creating || (giftMode && !giftUsername.trim())}
                      className="btn-brutal w-full"
                      data-testid={`vip-buy-${p.id}`}
                    >
                      {creating ? "Creating order..." : giftMode ? `Gift ${p.sol} SOL →` : (
                        <span className="flex items-center justify-center gap-2">
                          <Coins className="w-4 h-4" /> Pay {p.sol} SOL
                        </span>
                      )}
                    </button>
                    {!giftMode && gbpFor(p.id) != null && (
                      <button
                        onClick={() => payWithCard(p.id)}
                        disabled={cardBusy === p.id}
                        className="btn-ghost w-full mt-2"
                        data-testid={`vip-buy-card-${p.id}`}
                      >
                        {cardBusy === p.id ? (
                          <span className="flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <CreditCard className="w-4 h-4" /> Pay £{gbpFor(p.id)} with card
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {isVip && giftMode && (
            <div className="grid grid-cols-12 gap-6 mb-10">
              {plans.map((p) => (
                <div key={p.id} className="col-span-12 md:col-span-6 panel-bright p-8" data-testid={`vip-plan-${p.id}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                    GIFT {p.label.toUpperCase()}
                  </div>
                  <div className="flex items-baseline gap-3 mt-3">
                    <div className="font-display text-7xl text-[var(--primary)] glow">{p.sol}</div>
                    <div className="font-mono text-xl text-white">SOL</div>
                  </div>
                  <p className="text-sm text-[var(--text-muted)] mt-4 mb-6">{p.description}</p>
                  <button
                    onClick={() => startOrder(p.id)}
                    disabled={creating || !giftUsername.trim()}
                    className="btn-brutal w-full"
                    data-testid={`vip-buy-${p.id}`}
                  >
                    {creating ? "Creating order..." : `Gift ${p.sol} SOL →`}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Payment flow */}
      {order && (
        <div className="panel-bright p-6 mb-10" data-testid="vip-order-panel">
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="tape-label">PAYMENT INSTRUCTIONS</span>
              <div className="font-display text-3xl mt-2">
                Send <span className="text-[var(--primary)] glow" data-testid="order-amount">{order.amount_sol}</span> SOL
              </div>
              <div className="text-xs font-mono text-[var(--text-muted)] mt-1">
                Order {order.order_id.slice(0, 8)} · expires in {Math.floor(order.expires_in_seconds / 60)} min
              </div>
            </div>
            <button
              onClick={() => setOrder(null)}
              className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--danger)]"
              data-testid="order-cancel"
            >
              Cancel order
            </button>
          </div>

          <div className="grid grid-cols-12 gap-6">
            {/* Instructions */}
            <div className="col-span-12 lg:col-span-7 space-y-4">
              <Step
                n="01"
                title="Open your Solana wallet"
                body="Phantom, Solflare, Backpack — anything that can send SOL on mainnet."
              />
              <Step
                n="02"
                title="Send EXACTLY this amount"
                body={
                  <div className="mt-2 flex items-center gap-2">
                    <code className="bg-[var(--surface)] border border-[var(--border-bright)] px-3 py-2 font-mono text-lg text-[var(--primary)] flex-1">
                      {order.amount_sol} SOL
                    </code>
                    <button
                      onClick={() => copy(String(order.amount_sol), "amount")}
                      className="btn-ghost text-xs px-3"
                      data-testid="copy-amount"
                    >
                      {copied === "amount" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                }
              />
              <Step
                n="03"
                title="To this wallet address"
                body={
                  <div className="mt-2 flex items-center gap-2">
                    <code className="bg-[var(--surface)] border border-[var(--border-bright)] px-3 py-2 font-mono text-xs text-white break-all flex-1">
                      {order.wallet_address}
                    </code>
                    <button
                      onClick={() => copy(order.wallet_address, "wallet")}
                      className="btn-ghost text-xs px-3"
                      data-testid="copy-wallet"
                    >
                      {copied === "wallet" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                }
              />
              <Step
                n="04"
                title="Paste your transaction signature below"
                body="Once the tx confirms (~10-30 seconds), copy the signature from your wallet's activity and paste it. We'll verify on-chain and grant VIP instantly."
              />
            </div>

            {/* Signature input */}
            <div className="col-span-12 lg:col-span-5">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">
                Transaction Signature
              </label>
              <textarea
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Paste the tx signature from your Solana wallet…"
                rows={5}
                className="brutal font-mono text-xs"
                data-testid="verify-signature-input"
              />
              <button
                onClick={verify}
                disabled={verifying || !signature.trim()}
                className="btn-brutal w-full mt-4"
                data-testid="verify-button"
              >
                {verifying ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Verifying on-chain…
                  </span>
                ) : (
                  "Verify & Ascend →"
                )}
              </button>
              <p className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] leading-relaxed">
                We check the Solana mainnet directly. Send the EXACT amount shown — a random suffix is added so
                we can match your payment. Do not modify the amount.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] text-center">
        All payments on-chain to Solana wallet <span className="text-[var(--primary)]">{wallet.slice(0, 4)}…{wallet.slice(-4)}</span> — no third-party processor, no card, no data.
      </div>
    </div>
  );
}

const Step = ({ n, title, body }) => (
  <div className="grid grid-cols-12 gap-3">
    <div className="col-span-1 font-display text-3xl text-[var(--primary)] leading-none">{n}</div>
    <div className="col-span-11">
      <div className="font-mono text-sm text-white uppercase tracking-widest">{title}</div>
      <div className="text-sm text-[var(--text-muted)] mt-1">{body}</div>
    </div>
  </div>
);
